import { ChangeDetectorRef, Component, Injector, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { DocumentsActions } from '@modeso/aumico-lib-annualreport-fe/actions/workflow-documents.actions';
import { InventoryData, MMTask } from '@modeso/aumico-lib-annualreport-fe/dtos/inventory-data.dto';
import { ITasksExportTableHeaders } from '@modeso/aumico-lib-annualreport-fe/dtos/tasks-export-table-headers.dto';
import { WorkflowDocumentsProvider } from '@modeso/aumico-lib-annualreport-fe/provider/workflow-documets.provider';
import { PersonsProvider } from '@modeso/aumico-lib-companies-fe';
import {
    DocumentDto,
    ExportTasksExcelData,
    selectMandateManagementWorkflowOf,
    SubjectType,
    TaskState,
    WorkflowsActions,
} from '@modeso/aumico-lib-cpmm-fe';
import { setScrollToTextState } from '@modeso/aumico-lib-cpmm-fe/actions/ui.actions';
import { FilePreviewStatus } from '@modeso/aumico-lib-cpmm-fe/enums/FilePreviewStatus.enum';
import { INameId } from '@modeso/types__aumico-lib-core-be';
import { IPersonDTO } from '@modeso/types__aumico-lib-companies-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { I18NextService } from 'angular-i18next';
import { cloneDeep } from 'lodash-es';
import { Moment } from 'moment';
import { combineLatest, Observable, of, Subscription } from 'rxjs';
import { filter, first, map, switchMap, take } from 'rxjs/operators';
import { SyncActions } from '../../../../../../aumico-lib-cpmm-fe/src/lib/actions/sync.actions';
import { TasksOverviewActions } from '../../../../../../aumico-lib-cpmm-fe/src/lib/actions/tasks.actions';
import { CreateAnnotationDto } from '../../../../../../aumico-lib-cpmm-fe/src/lib/dtos/create-annotation-dto.interface';
import { CreateTaskRequestDto } from '../../../../../../aumico-lib-cpmm-fe/src/lib/dtos/create-task-dto.interface';
import { UpdateAnnotationDto } from '../../../../../../aumico-lib-cpmm-fe/src/lib/dtos/update-annotation-dto.interface';
import { UpdateTaskDto } from '../../../../../../aumico-lib-cpmm-fe/src/lib/dtos/update-task-dto.interface';
import { ExportTasksFilterState } from '../../../../../../aumico-lib-cpmm-fe/src/lib/enums/export-tasks-filter.enum';
import { WorkflowArea } from '../../../../../../aumico-lib-cpmm-fe/src/lib/enums/workflow-area.enum';
import { Annotation } from '../../../../../../aumico-lib-cpmm-fe/src/lib/interfaces/annotation.interface';
import { TaskMetaInformation } from '../../../../../../aumico-lib-cpmm-fe/src/lib/interfaces/task-meta-information.interface';
import { AnnotationsProvider } from '../../../../../../aumico-lib-cpmm-fe/src/lib/providers/annotations.provider';
import {
    selectDocuments,
    selectMandateManagementTasksByProjectId,
} from '../../../../../../aumico-lib-cpmm-fe/src/lib/reducers/tasks.reducer';
import {
    PersonProjectDto,
    PersonProjectsActions,
    PersonProjectsService,
    selectPersonProjectsEntities,
} from '../../../../../../aumico-lib-projects-fe/src/lib';
import { ProjectStatus } from '../../../../../../aumico-lib-projects-fe/src/lib/enums/project-status.enum';
import { GetOrganizationDto, GetUserDto, OrganizationProvider } from '../../../../../../aumico-lib-users-fe/src/public-api';
import { Paths } from '../../../enums/paths.enum';
import { RouteLabels } from '../../../enums/routeLabels.enum';
import { BasePageComponent } from '../../../pages/base.page';
import { taxYearOf } from '../helpers/person-projects-table.helper';

/**
 * REV-22155 — mandate management page of a person project.
 *
 * A slim own page: `cpmm/mandate-management` is bound to an annual report (name, address, date for the
 * exports, document expiry) and stays untouched. Same look — `workflow-form`, sync banner, bulk actions;
 * the data comes from the person project and the person. Header: back arrow, title, one tab. Tasks, comments and documents use the
 * `/tasks/…` endpoints; loading, adding a task, syncing and the ZIP export go to `/person-projects/…`.
 */
@UntilDestroy()
@Component({
    selector: 'app-person-mandate-management-page',
    templateUrl: './person-mandate-management.page.html',
    styleUrls: ['./person-mandate-management.page.scss'],
})
export class PersonMandateManagementPage extends BasePageComponent implements OnInit {
    readonly WorkflowArea = WorkflowArea;
    projectId = '';
    title = '';
    backRoute: string[] = [];
    readonly navigationTabs = [{ routerLink: '.', tabLabelKey: RouteLabels.REPORT_MANDATE_MANAGEMENT }];

    tasks$: Observable<TaskMetaInformation[]> = of([]);
    tasks: TaskMetaInformation[] = [];
    assigneeOptions: INameId[] = [];
    deletedUsers: GetUserDto[] = [];
    annotationsMap: { [taskId: string]: Annotation[] } = {};
    documentsMap: { [taskId: string]: DocumentDto[] } = {};
    documentsInProgressUpload: { [taskId: string]: string[] } = {};
    documentExpirationDate: Date | undefined;

    isSyncNeeded = false;
    /** Time of the last "anwenden" / "nicht anwenden" on this page; a stale project copy cannot undo it. */
    private syncedAt: Date | undefined;
    isSyncingWorkflow = false;
    private templateUpdatedAt: Date | undefined;
    private project: PersonProjectDto | undefined;
    private person: IPersonDTO | undefined;
    private organization: GetOrganizationDto | undefined;
    private organizationUsers: INameId[] = [];
    private openFileWindow: Window | null = null;
    private readonly pollSubs = new Map<string, Subscription>();
    private readonly pollTimeouts = new Map<string, ReturnType<typeof setTimeout>>();
    private readonly pollingDelays = [500, 500, 1000, 1500, 2500, 2500, 2500, 2500, 2500, 2500];

    // Bulk edit — as on the company page
    readonly bulkActions = [
        { id: 'STATUS_CHANGE', translationKey: 'aumico_tasks_overview_bulk_edit_status_action_bar' },
        { id: 'ASSIGNEE_CHANGE', translationKey: 'aumico_tasks_overview_bulk_edit_assignee_action_bar' },
        { id: 'DUE_DATE_CHANGE', translationKey: 'aumico_tasks_overview_bulk_edit_due_date_action_bar' },
    ];
    readonly deleteAction = { id: 'DELETE_TASKS', translationKey: 'aumico_tasks_overview_bulk_edit_delete_action_bar' };
    openBulkEditStatus = false;
    openBulkEditAssignee = false;
    openBulkEditDueDate = false;
    openBulkDeleteConfirmationDialog = false;
    readonly possibleStatus = [
        { id: TaskState.OPEN, name: this.localizedTexts.t('aumico_pm_tasks_status_open') as string },
        { id: TaskState.IN_PROGRESS, name: this.localizedTexts.t('aumico_pm_tasks_status_in_progress') as string },
        { id: TaskState.IN_REVIEW, name: this.localizedTexts.t('aumico_pm_tasks_status_in_review') as string },
        { id: TaskState.DONE, name: this.localizedTexts.t('aumico_pm_tasks_status_done') as string },
    ];
    readonly bulkEditStatusControl = this.fb.control<{ id: TaskState; name: string } | null>(null);
    readonly bulkEditAssigneeControl = this.fb.control<INameId | null>(null);
    readonly bulkEditDueDateControl = this.fb.control<Date | null>(null);
    selectAll = new Set<string>();
    sectionToggled = new Set<string>();
    selectedTaskIds = new Set<string>();

    constructor(
        injector: Injector,
        private readonly store: Store,
        private readonly annotationsProvider: AnnotationsProvider,
        private readonly workflowDocumentsProvider: WorkflowDocumentsProvider,
        private readonly organizationProvider: OrganizationProvider,
        private readonly personsProvider: PersonsProvider,
        private readonly personProjectsService: PersonProjectsService,
        private readonly localizedTexts: I18NextService,
        private readonly fb: FormBuilder,
        private readonly cdr: ChangeDetectorRef,
    ) {
        super(injector);
    }

    override ngOnInit(): void {
        super.ngOnInit();
        this.projectId = this.activatedRoute.snapshot.params['projectId'];
        this.backRoute = [
            `/${this.localStorageService.getCurrentLanguage()}/${Paths.HOME}/${Paths.PROJECT}/${Paths.PERSON_PROJECTS}`,
        ];

        this.store.dispatch(PersonProjectsActions.loadPersonProject({ projectId: this.projectId }));
        this.store.dispatch(WorkflowsActions.loadPersonMandateManagementWorkflow());
        this.store.dispatch(
            TasksOverviewActions.loadProjectTasks({
                projectId: this.projectId,
                filter: { offset: 0, limit: 1000 },
                subjectType: SubjectType.PERSON,
            }),
        );
        this.organizationProvider.dispatchGetUsers(true);
        this.tasks$ = this.store.select(selectMandateManagementTasksByProjectId(this.projectId));

        this.subscribeProjectAndPerson();
        this.subscribeTasksAndAnnotations();
        this.subscribeUsers();
        this.subscribeDocuments();
        this.subscribeSync();
        this.scrollToRequestedSection();
    }

    // ── Project, person, sync hint ───────────────────────────────────────────────────────────────

    private subscribeProjectAndPerson(): void {
        this.store
            .select(selectPersonProjectsEntities)
            .pipe(
                map((entities) => entities[this.projectId]),
                filter((project): project is PersonProjectDto => project != null),
                untilDestroyed(this),
            )
            .subscribe((project) => {
                const loadPerson = this.project?.personId !== project.personId;
                this.project = project;
                // "Brunner Laura – Steuerperiode 2025"
                this.title = `${project.personName} – ${this.localizedTexts.t('aumico_person_projects_tax_period', {
                    year: taxYearOf(project),
                })}`;
                // Documents of earlier years count until the period end, like the balance sheet date for companies.
                this.documentExpirationDate = new Date(project.periodEnd);
                if (loadPerson) {
                    this.personsProvider.dispatchPersonById(project.personId);
                }
                this.updateSyncNeeded();
            });

        this.personsProvider
            .getSelectedPerson$()
            .pipe(
                filter((person): person is IPersonDTO => person != null && person._id === this.project?.personId),
                untilDestroyed(this),
            )
            .subscribe((person) => (this.person = person));

        this.store
            .select(selectMandateManagementWorkflowOf(SubjectType.PERSON))
            .pipe(untilDestroyed(this))
            .subscribe((workflow) => {
                this.templateUpdatedAt = workflow ? new Date(workflow.updatedAt) : undefined;
                this.updateSyncNeeded();
            });

        this.organizationProvider
            .getCurrentOrganization$()
            .pipe(
                filter((org) => org != null),
                untilDestroyed(this),
            )
            .subscribe((org) => (this.organization = cloneDeep(org)));
    }

    /** Hint when the person template changed after the last sync — only for active projects (like companies). */
    private updateSyncNeeded(): void {
        const storedSync = this.project?.lastTimeSynchronizedMM ? new Date(this.project.lastTimeSynchronizedMM) : undefined;
        const lastSync = this.syncedAt && (!storedSync || this.syncedAt > storedSync) ? this.syncedAt : storedSync;
        this.isSyncNeeded =
            this.project?.projectStatus === ProjectStatus.ACTIVE &&
            lastSync != null &&
            this.templateUpdatedAt != null &&
            lastSync < this.templateUpdatedAt;
    }

    onSync(): void {
        this.isSyncingWorkflow = true;
        this.dispatchSync(true);
    }

    onDismiss(): void {
        this.dispatchSync(false);
    }

    /** "anwenden" / "nicht anwenden" hide the hint at once; the reloaded project confirms it. */
    private dispatchSync(sync: boolean): void {
        this.syncedAt = new Date();
        this.updateSyncNeeded();
        this.store.dispatch(
            SyncActions.syncProjectWorkflow({
                projectId: this.projectId,
                workflowArea: WorkflowArea.MANDATE_MANAGEMENT,
                sync,
                subjectType: SubjectType.PERSON,
            }),
        );
    }

    private subscribeSync(): void {
        this.actionsListener$
            .pipe(ofType(SyncActions.projectWorkflowSynced, SyncActions.projectWorkflowSyncFailure), untilDestroyed(this))
            .subscribe((action) => {
                this.isSyncingWorkflow = false;
                // A failed sync shows the hint again (it was hidden on the click).
                if (action.type === SyncActions.projectWorkflowSyncFailure.type) {
                    this.syncedAt = undefined;
                    this.updateSyncNeeded();
                }
                this.store.dispatch(PersonProjectsActions.loadPersonProject({ projectId: this.projectId }));
            });
    }

    /** `?section=` from the projects table, `?taskId=` from a notification: scroll to the section. */
    private scrollToRequestedSection(): void {
        this.activatedRoute.queryParamMap
            .pipe(
                map((params) => ({ section: params.get('section'), taskId: params.get('taskId') })),
                filter(({ section, taskId }) => !!section || !!taskId),
                switchMap(({ section, taskId }) =>
                    this.tasks$.pipe(
                        filter((tasks) => tasks != null && tasks.length > 0),
                        first(),
                        map((tasks) => section ?? tasks.find((t) => t.id === taskId)?.sectionName ?? null),
                    ),
                ),
                filter((section): section is string => !!section),
                untilDestroyed(this),
            )
            .subscribe((section) => this.store.dispatch(setScrollToTextState({ text: section })));
    }

    // ── Tasks, comments, users ───────────────────────────────────────────────────────────────────

    private subscribeTasksAndAnnotations(): void {
        combineLatest([this.tasks$, this.annotationsProvider.getTaskAnnotations$()])
            .pipe(untilDestroyed(this))
            .subscribe(([tasks, annotations]) => {
                this.tasks = cloneDeep(tasks ?? []);
                for (const task of this.tasks) {
                    this.annotationsMap[task.id] = annotations
                        .filter((a) => a.taskId === task.id)
                        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
                }
            });
    }

    private subscribeUsers(): void {
        this.organizationProvider
            .getUsers()
            .pipe(
                filter((users) => users.length > 0),
                untilDestroyed(this),
            )
            .subscribe((users) => {
                this.deletedUsers = users.filter((u) => u.isDeleted);
                const active = users
                    .filter((u) => u != null && !u.isInvitee && !u.isDeleted)
                    .map((u) => ({ id: u.id, name: `${u.firstName} ${u.lastName}`, isMe: u.isMe }));
                this.organizationUsers = active;
                const me = active.filter((u) => u.isMe);
                this.assigneeOptions = [
                    { id: null as unknown as string, name: this.localizedTexts.t('aumico_tasks_unassigned') as string },
                    ...me,
                    ...active.filter((u) => !u.isMe),
                ];
            });
    }

    addNewTask({ createTaskDto }: { createTaskDto: CreateTaskRequestDto }): void {
        this.store.dispatch(
            TasksOverviewActions.createTask({
                projectId: this.projectId,
                createTaskRequestDto: createTaskDto,
                subjectType: SubjectType.PERSON,
            }),
        );
    }

    updateTask({ taskId, updateTaskDto }: { taskId: string; updateTaskDto: UpdateTaskDto }): void {
        this.store.dispatch(TasksOverviewActions.updateTask({ taskId, updateTaskDto }));
    }

    bulkUpdateTasks(event: { taskIds: string[]; updateTaskDto: UpdateTaskDto }): void {
        this.store.dispatch(TasksOverviewActions.bulkUpdateTasks(event));
    }

    deleteTask({ taskId }: { taskId: string }): void {
        this.store.dispatch(TasksOverviewActions.deleteTask({ taskId }));
    }

    onAnnotationAdded(event: { taskId: string; annotation: CreateAnnotationDto }): void {
        this.annotationsProvider.dispatchCreateAnnotation(event.taskId, event.annotation);
    }

    onAnnotationChanged(event: { annotationId: string; annotation: UpdateAnnotationDto }): void {
        this.annotationsProvider.dispatchUpdateAnnotation(event.annotationId, event.annotation);
    }

    onAnnotationRemoved(annotationId: string): void {
        this.annotationsProvider.dispatchDeleteAnnotation(annotationId);
    }

    // ── Documents (same handling as the company page) ────────────────────────────────────────────

    private subscribeDocuments(): void {
        this.store
            .select(selectDocuments)
            .pipe(untilDestroyed(this))
            .subscribe((documents) => {
                for (const doc of documents) {
                    for (const key of [doc.taskId, doc.staticTaskReference].filter((k): k is string => !!k)) {
                        const bucket = (this.documentsMap[key] ??= []);
                        if (!bucket.some((existing) => existing.id === doc.id)) {
                            bucket.push(doc);
                        }
                    }
                }
            });

        this.actionsListener$
            .pipe(ofType(DocumentsActions.updateDocumentMetadataSuccess), untilDestroyed(this))
            .subscribe(({ document }) => {
                for (const taskId of Object.keys(this.documentsMap)) {
                    const index = this.documentsMap[taskId].findIndex((d) => d.id === document?.id);
                    if (index !== -1 && document) {
                        this.documentsMap[taskId][index] = { ...document };
                        this.documentsMap = { ...this.documentsMap };
                        this.cdr.detectChanges();
                        return;
                    }
                }
            });

        this.actionsListener$
            .pipe(ofType(DocumentsActions.openFileSuccess), untilDestroyed(this))
            .subscribe(({ fileId, url }) => {
                this.openFileWindow = window.open(url, '_blank');
                setTimeout(() => this.workflowDocumentsProvider.dispatchRevokeFileUrl(fileId), 1000);
            });

        this.actionsListener$
            .pipe(ofType(DocumentsActions.openFileFailure), untilDestroyed(this))
            .subscribe(() => {
                if (this.openFileWindow && !this.openFileWindow.closed) {
                    this.openFileWindow.close();
                }
                this.openFileWindow = null;
            });

        this.actionsListener$
            .pipe(ofType(DocumentsActions.checkPreviewStatusSuccess), untilDestroyed(this))
            .subscribe(({ fileId, status }) => {
                const document = this.findDocument(fileId);
                if (document) {
                    document.previewStatus = status;
                }
            });

        this.actionsListener$
            .pipe(ofType(DocumentsActions.loadPreviewUrlSuccess), untilDestroyed(this))
            .subscribe(({ fileId, url }) => {
                const document = this.findDocument(fileId);
                if (document) {
                    document.previewUrl = url;
                }
            });

        this.actionsListener$
            .pipe(ofType(DocumentsActions.uploadDocumentSuccess), untilDestroyed(this))
            .subscribe(({ documents }) => this.onDocumentsAdded(documents));
    }

    onDocumentRemoved(document: DocumentDto): void {
        this.workflowDocumentsProvider.dispatchDeleteDocument(document.id, this.projectId);
        for (const taskId of Object.keys(this.documentsMap)) {
            this.documentsMap[taskId] = this.documentsMap[taskId].filter((d) => d.id !== document.id);
        }
    }

    onDocumentsAdded(documents: DocumentDto[]): void {
        const normalize = (name: string): string =>
            name
                .normalize('NFD')
                .replace(/[^a-zA-Z0-9]/g, '')
                .toLowerCase();
        for (const document of documents) {
            const originalName = normalize(document.name);
            const bucket = (this.documentsMap[document.taskId] ??= []);
            if (!bucket.some((d) => d.id === document.id)) {
                document.name = document.name.replace(/\.[^/.]+$/, '');
                bucket.unshift(document);
                this.documentsMap = { ...this.documentsMap };
            }
            if (this.documentsInProgressUpload[document.taskId]) {
                this.documentsInProgressUpload[document.taskId] = this.documentsInProgressUpload[document.taskId].filter(
                    (fileName) => normalize(fileName) !== originalName,
                );
                this.documentsInProgressUpload = { ...this.documentsInProgressUpload };
            }
        }
    }

    onDocumentsAddedInProgress({ taskId, fileNames }: { taskId: string; fileNames: string[] }): void {
        (this.documentsInProgressUpload[taskId] ??= []).push(...fileNames);
        this.documentsInProgressUpload = { ...this.documentsInProgressUpload };
    }

    onOpenFile(fileId: string): void {
        this.workflowDocumentsProvider.dispatchOpenFile(fileId);
    }

    onDownloadFile(fileId: string, documentName: string): void {
        this.workflowDocumentsProvider.dispatchDownloadFile(fileId, documentName);
    }

    onStartPolling(fileId: string): void {
        const delays = [...this.pollingDelays];
        const tick = (): void => {
            const delay = delays.shift();
            if (delay == null) {
                this.onStopPolling(fileId);
                return;
            }
            const timeout = setTimeout(() => {
                const sub = this.actionsListener$
                    .pipe(
                        ofType(DocumentsActions.checkPreviewStatusSuccess),
                        filter((a) => a.fileId === fileId),
                        take(1),
                        untilDestroyed(this),
                    )
                    .subscribe(({ status }) => {
                        if (status === FilePreviewStatus.GENERATED) {
                            this.workflowDocumentsProvider.dispatchLoadPreviewUrl(fileId);
                            this.onStopPolling(fileId);
                        } else if (status === FilePreviewStatus.PENDING || status === FilePreviewStatus.SCANNING) {
                            tick();
                        } else if (status === FilePreviewStatus.NOT_STARTED) {
                            this.workflowDocumentsProvider.dispatchRequestPreviewGeneration(fileId);
                            tick();
                        } else {
                            this.onStopPolling(fileId);
                        }
                    });
                this.pollSubs.set(fileId, sub);
                this.workflowDocumentsProvider.dispatchCheckPreviewStatus(fileId);
            }, delay);
            this.pollTimeouts.set(fileId, timeout);
        };
        tick();
    }

    onStopPolling(fileId: string): void {
        this.pollSubs.get(fileId)?.unsubscribe();
        this.pollSubs.delete(fileId);
        const timeout = this.pollTimeouts.get(fileId);
        if (timeout) {
            clearTimeout(timeout);
        }
        this.pollTimeouts.delete(fileId);
    }

    private findDocument(fileId: string): DocumentDto | undefined {
        return Object.values(this.documentsMap)
            .flat()
            .find((doc) => doc.fileId === fileId);
    }

    private documentNamesOf(task: TaskMetaInformation): string[] {
        const docs = [
            ...(this.documentsMap[task.id] ?? []),
            ...(task.staticTaskReference ? this.documentsMap[task.staticTaskReference] ?? [] : []),
        ];
        return docs.filter((doc, i, all) => all.findIndex((d) => d.id === doc.id) === i).map((doc) => doc.name);
    }

    // ── Exports: name, address and date from the person and the person project ───────────────────

    /** `Brunner Laura_<Organisation>_31_12_2025` */
    private exportFileBase(): string {
        const end = new Date(this.project?.periodEnd ?? Date.now());
        const date = `${String(end.getUTCDate()).padStart(2, '0')}_${String(end.getUTCMonth() + 1).padStart(2, '0')}_${end.getUTCFullYear()}`;
        return `${this.project?.personName ?? ''}_${this.organization?.name ?? ''}_${date}`;
    }

    exportZip(): void {
        this.personProjectsService
            .exportPersonProjectDocuments(this.projectId, this.buildInventoryData())
            .pipe(untilDestroyed(this))
            .subscribe((blob) => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${this.exportFileBase()}.zip`;
                a.click();
                window.URL.revokeObjectURL(url);
            });
    }

    private buildInventoryData(): InventoryData {
        const userName = (id: string | null): string => (id ? this.organizationUsers.find((u) => u.id === id)?.name ?? id : '');
        const mmTasks: MMTask[] = this.tasks
            .filter((task) => this.documentNamesOf(task).length > 0)
            .map((task) => ({
                taskSection: task.sectionName || '',
                taskDescription: task.description || '',
                document: this.documentNamesOf(task).join(', '),
                assignee: userName(task.assignedTo),
            }));
        return {
            generalData: {
                companyName: this.project?.personName ?? '',
                companyAdress: this.person?.street ?? '',
                companyZIP: this.person?.zip ?? '',
                companyCity: this.person?.city ?? '',
            },
            hasCP: false,
            hasMM: mmTasks.length > 0,
            MMdata:
                mmTasks.length > 0
                    ? {
                          tableHeaders: {
                              section: this.localizedTexts.t('aumico_file_export_section_title') as string,
                              descriptionTitle: this.localizedTexts.t('aumico_file_export_description_title') as string,
                              documentTitle: this.localizedTexts.t('aumico_file_export_document_title') as string,
                              assigneeTitle: this.localizedTexts.t('aumico_projects_overview_filter_assignee_label') as string,
                          },
                          tasks: mmTasks,
                      }
                    : null,
            CPData: null,
        };
    }

    exportTasksExcel(tasksFilter: (TaskState | ExportTasksFilterState)[]): void {
        const clean = (text: string | null | undefined): string =>
            (text ?? '')
                .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '')
                .replace(/[￾￿]/g, '')
                .replace(/\\[bfnrtv]/g, '')
                .trim();
        const stateLabel: Record<number, string> = {
            [TaskState.OPEN]: 'aumico_pm_tasks_status_open',
            [TaskState.DONE]: 'aumico_pm_tasks_status_done',
            [TaskState.NOT_APPLICABLE]: 'aumico_ui_not_applicable',
            [TaskState.IN_REVIEW]: 'aumico_pm_tasks_status_in_review',
            [TaskState.IN_PROGRESS]: 'aumico_pm_tasks_status_in_progress',
        };

        const sections = new Map<string, { name: string; tasks: ExportTasksExcelData[] }>();
        for (const task of this.tasks) {
            if (!tasksFilter.includes(task.taskState)) {
                continue;
            }
            // The Excel rows carry display texts in the task fields (as on the company page).
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const taskData: any = cloneDeep(task);
            const sectionId = task.sectionId || task.sectionName || 'all';
            if (!sections.has(sectionId)) {
                sections.set(sectionId, { name: clean(task.sectionName || sectionId), tasks: [] });
            }
            const [year, month, day] = String(task.dueDate).split('T')[0].split('-');
            taskData.dueDate = `${day}.${month}.${year}`;
            taskData.taskState = this.localizedTexts.t(stateLabel[task.taskState] ?? 'aumico_pm_tasks_status_open') as string;
            taskData.assignedTo = clean(
                this.organizationUsers.find((u) => u.id === task.assignedTo)?.name ??
                    (this.localizedTexts.t('aumico_tasks_unassigned') as string),
            );
            taskData.description = clean(taskData.description);
            sections.get(sectionId)!.tasks.push({
                task: taskData,
                documentNames: this.documentNamesOf(task).map(clean),
                annotations: this.annotationsMap[task.id]?.map((a) => clean(a.content)) ?? [],
            });
        }

        const t = (key: string): string => this.localizedTexts.t(key).toString();
        const tableHeaders: ITasksExportTableHeaders = {
            description: t('aumico_pm_tasks_task_table_header'),
            assignedTo: t('aumico_pm_tasks_responsible_table_header'),
            dueDate: t('aumico_pm_tasks_due_date_table_header'),
            taskState: t('aumico_pm_tasks_status_table_header'),
            documents: t('aumico_ui_documents'),
            annotations: t('aumico_ui_annotations'),
            sectionName: t('aumico_pm_tasks_section_table_header'),
            companyName: t('aumico_tasks_col_mandant'),
        };
        const end = new Date(this.project?.periodEnd ?? Date.now());
        const tableTitle = `${clean(this.project?.personName)} ${String(end.getUTCDate()).padStart(2, '0')}.${String(
            end.getUTCMonth() + 1,
        ).padStart(2, '0')}.${end.getUTCFullYear()}`;

        this.workflowDocumentsProvider.dispatchExportTasksAsExcel(
            tableHeaders,
            Array.from(sections.entries()).map(([id, { name, tasks }]) => ({ id, name, tasks })),
            `${clean(this.project?.personName)}_${clean(this.organization?.name)}_${t('aumico_reports_cpmm_tasks')}`,
            tasksFilter,
            tableTitle,
            this.organization as GetOrganizationDto,
        );
    }

    // ── Selection and bulk actions (as on the company page) ──────────────────────────────────────

    toggleCheckAll(tab: string): void {
        if (this.selectAll.has(tab)) {
            this.clearSelections();
            return;
        }
        for (const task of this.tasks) {
            if (task.taskType === tab) {
                this.sectionToggled.add(task.sectionId);
                this.selectedTaskIds.add(task.id);
            }
        }
        this.selectAll.add(tab);
    }

    toggleSectionSelectAll(section: { id: string; tasks: TaskMetaInformation[] }): void {
        const select = !this.sectionToggled.has(section.id);
        if (select) {
            this.sectionToggled.add(section.id);
        } else {
            this.sectionToggled.delete(section.id);
        }
        for (const task of this.tasks.filter((t) => t.sectionId === section.id)) {
            if (select) {
                this.selectedTaskIds.add(task.id);
            } else {
                this.selectedTaskIds.delete(task.id);
            }
        }
        this.updateSelectAllState(section.tasks[0]?.taskType);
    }

    toggleCheckTask(task: TaskMetaInformation): void {
        if (this.selectedTaskIds.has(task.id)) {
            this.selectedTaskIds.delete(task.id);
            this.sectionToggled.delete(task.sectionId);
        } else {
            this.selectedTaskIds.add(task.id);
        }
        this.updateSelectAllState(task.taskType);
    }

    private updateSelectAllState(tab: string | undefined): void {
        if (tab == null) {
            return;
        }
        const all = this.tasks.filter((t) => t.taskType === tab).every((t) => this.selectedTaskIds.has(t.id));
        if (all && this.tasks.length > 0) {
            this.selectAll.add(tab);
        } else {
            this.selectAll.delete(tab);
        }
    }

    private clearSelections(): void {
        this.selectAll.clear();
        this.sectionToggled.clear();
        this.selectedTaskIds.clear();
    }

    onActionClicked(actionId: string): void {
        this.openBulkEditStatus = actionId === 'STATUS_CHANGE';
        this.openBulkEditAssignee = actionId === 'ASSIGNEE_CHANGE';
        this.openBulkEditDueDate = actionId === 'DUE_DATE_CHANGE';
        this.openBulkDeleteConfirmationDialog = actionId === 'DELETE_TASKS';
        if (actionId === 'CLOSE_BULK_EDIT') {
            this.clearSelections();
        }
    }

    cancelDeleteBulkTasks(): void {
        this.openBulkDeleteConfirmationDialog = false;
    }

    confirmDeleteBulkTasks(): void {
        this.openBulkDeleteConfirmationDialog = false;
        this.store.dispatch(TasksOverviewActions.bulkDeleteTasks({ taskIds: Array.from(this.selectedTaskIds) }));
        this.clearSelections();
    }

    onEditStatusConfirm(): void {
        const status = this.bulkEditStatusControl.value?.id;
        if (status != null) {
            this.openBulkEditStatus = false;
            this.bulkUpdateSelected({ taskState: status });
            this.bulkEditStatusControl.reset();
        }
    }

    onEditAssigneeConfirm(): void {
        const assignee = this.bulkEditAssigneeControl.value;
        if (assignee) {
            this.openBulkEditAssignee = false;
            this.bulkUpdateSelected({ assignedTo: assignee.id });
            this.bulkEditAssigneeControl.reset();
        }
    }

    onEditDueDateConfirm(): void {
        const dueDate = this.bulkEditDueDateControl.value;
        if (dueDate) {
            this.openBulkEditDueDate = false;
            this.bulkUpdateSelected({ dueDate });
            this.bulkEditDueDateControl.reset();
        }
    }

    bulkEditDueDate(m: Moment): void {
        this.bulkEditDueDateControl.setValue(new Date(Date.UTC(m.year(), m.month(), m.date())));
    }

    private bulkUpdateSelected(updateTaskDto: UpdateTaskDto): void {
        this.store.dispatch(TasksOverviewActions.bulkUpdateTasks({ taskIds: Array.from(this.selectedTaskIds), updateTaskDto }));
        this.clearSelections();
    }
}
